import { useState } from "react";
import { Upload } from "antd";
import { UploadCloud, Download, FileSpreadsheet, AlertTriangle, CheckCircle2 } from "lucide-react";
import * as XLSX from "xlsx";

import ToastMsg from "../../../components/common/ToastMsg";
import { Button } from "../../../components/ui/button";
import { WizardFooter, PRIMARY_BTN } from "./WizardFooter";
import { getCSVData } from "../../../components/common/CommonFunctions";
import { EMPLOYEE_MIGRATION_TEMPLATE } from "../../../constants/api";
import {
    FIELDS,
    REQUIRED_HEADER,
    headerToKey,
    toIsoDate,
    rowHasErrors,
    ensureKeys,
} from "../migrationConfig";

const MAX_ROWS = 100;
const HEADER_ROW = 2; // row 0 = instructions, row 1 = blank, row 2 = headers
const DATE_KEYS = new Set(FIELDS.filter((f) => f.kind === "date").map((f) => f.key));

const UploadStep = ({ options, onParsed, onCancel }) => {
    const [parsing, setParsing] = useState(false);
    const [fileName, setFileName] = useState("");
    const [rows, setRows] = useState([]);

    const downloadTemplate = async () => {
        try {
            await getCSVData(EMPLOYEE_MIGRATION_TEMPLATE);
        } catch (e) {
            ToastMsg("error", e.message || "Could not download the template");
        }
    };

    const parseSheet = (file) => {
        setParsing(true);
        const reader = new FileReader();
        reader.onload = (event) => {
            try {
                const wb = XLSX.read(event.target.result, { type: "array" });
                const sheet = wb.Sheets[wb.SheetNames[0]];
                const matrix = XLSX.utils.sheet_to_json(sheet, { header: 1, blankrows: false });

                const headers = matrix[HEADER_ROW] || [];
                if (headers[0] !== REQUIRED_HEADER) {
                    ToastMsg("error", "This file doesn't match the downloaded template. Please use the template.");
                    setRows([]);
                    setFileName("");
                    return;
                }

                const keys = headers.map(headerToKey);
                const body = matrix.slice(HEADER_ROW + 1);
                const parsed = [];
                for (const raw of body) {
                    if (!raw || raw.every((c) => c === null || c === undefined || c === "")) continue;
                    const row = {};
                    keys.forEach((key, j) => {
                        if (!key) return;
                        let val = raw[j];
                        if (DATE_KEYS.has(key)) {
                            val = typeof val === "number"
                                ? XLSX.SSF.format("YYYY-MM-DD", val)
                                : toIsoDate(val);
                        } else if (typeof val === "string") {
                            val = val.trim() || null;
                        }
                        row[key] = val ?? null;
                    });
                    parsed.push(row);
                }

                if (parsed.length === 0) {
                    ToastMsg("error", "No employee rows found in the sheet.");
                    setRows([]);
                    setFileName("");
                    return;
                }
                if (parsed.length > MAX_ROWS) {
                    ToastMsg("error", `A migration can hold at most ${MAX_ROWS} employees.`);
                    setRows([]);
                    setFileName("");
                    return;
                }
                setRows(parsed);
            } catch (e) {
                ToastMsg("error", "Could not read the spreadsheet.");
                setRows([]);
                setFileName("");
            } finally {
                setParsing(false);
            }
        };
        reader.onerror = () => {
            ToastMsg("error", "Could not read the file.");
            setParsing(false);
        };
        reader.readAsArrayBuffer(file);
    };

    const beforeUpload = (file) => {
        const isCsv = file.type === "text/csv" || file.name.toLowerCase().endsWith(".csv");
        if (!isCsv) {
            ToastMsg("error", "Please upload a .csv file.");
            return Upload.LIST_IGNORE;
        }
        setFileName(file.name);
        parseSheet(file);
        return false; // we handle parsing ourselves; never auto-upload
    };

    const validCount = rows.filter((r) => !rowHasErrors(r, options)).length;
    const conflictCount = rows.length - validCount;

    return (
        <div>
            <div className="p-4 space-y-5">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <div>
                    <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-100">
                        Step 1 — Upload employee data
                    </h3>
                    <p className="text-xs text-muted-foreground mt-1">
                        Download the template, fill in up to {MAX_ROWS} employees, then upload the CSV.
                    </p>
                </div>
                <Button variant="outline" size="sm" onClick={downloadTemplate}>
                    <Download className="w-4 h-4 mr-1" /> Download Template
                </Button>
            </div>

            <Upload.Dragger
                accept=".csv"
                maxCount={1}
                multiple={false}
                showUploadList={false}
                beforeUpload={beforeUpload}
                disabled={parsing}
            >
                <p className="flex justify-center text-sidebar-primary">
                    <UploadCloud className="w-8 h-8" />
                </p>
                <p className="text-sm font-medium mt-2">
                    {parsing ? "Reading sheet…" : "Click or drag the filled CSV here"}
                </p>
                <p className="text-xs text-muted-foreground">Only .csv exported from the template</p>
            </Upload.Dragger>

            {fileName && rows.length > 0 && (
                <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4 flex flex-wrap items-center gap-4">
                    <span className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-200">
                        <FileSpreadsheet className="w-4 h-4 text-sidebar-primary" /> {fileName}
                    </span>
                    <span className="flex items-center gap-1 text-sm text-gray-600 dark:text-gray-300">
                        {rows.length} rows detected
                    </span>
                    <span className="flex items-center gap-1 text-sm text-green-600">
                        <CheckCircle2 className="w-4 h-4" /> {validCount} ready
                    </span>
                    {conflictCount > 0 && (
                        <span className="flex items-center gap-1 text-sm text-amber-600">
                            <AlertTriangle className="w-4 h-4" /> {conflictCount} need attention
                        </span>
                    )}
                </div>
            )}

            </div>

            <WizardFooter>
                <Button variant="outline" onClick={onCancel}>Cancel</Button>
                <Button className={PRIMARY_BTN} disabled={rows.length === 0 || parsing} onClick={() => onParsed(ensureKeys(rows))}>
                    Next: Review &amp; Fix
                </Button>
            </WizardFooter>
        </div>
    );
};

export default UploadStep;
